/**
 * Development-only persistence for Explore Unknown POI positions (localStorage).
 * Coordinates are **authoring space** (same as `exploreUnknownPortalLayout.js`: 1920×1080 canvas).
 *
 * Not used in production builds — callers must gate on `process.env.NODE_ENV === 'development'`.
 */

const STORAGE_KEY_PREFIX = 'exploreUnknownDevWaypoints_';

/** @typedef {'normal'|'altMinus1'|'altMinus2'|'fork'|'exitPreviewNormal'|'exitPreviewAltMinus1'|'exitPreviewAltMinus2'|'exitPreviewAlternative'} ExploreUnknownDevPathEditMode */

/** Must match `exploreUnknownPortalLayout.js` waypoint counts per surface (normal path). */
const WAYPOINT_COUNT_BY_SURFACE = {
  arrival: 5,
  cave: 5,
  desert: 6,
  fair: 7,
  lavender: 5,
  treetop: 1,
  boat: 5,
  pixelpast: 6,
  kendrex: 6,
  autobahn: 7,
  productionhell: 7,
  awakening: 8,
  /** The Core has no map POIs (linear mission backgrounds only). */
  core: 0,
};

const VALID_PATH_MODES = Object.freeze([
  'normal',
  'altMinus1',
  'altMinus2',
  'fork',
  'exitPreviewNormal',
  'exitPreviewAltMinus1',
  'exitPreviewAltMinus2',
  'exitPreviewAlternative',
]);

/**
 * @param {ExploreUnknownDevPathEditMode} pathMode
 * @returns {string}
 */
function normalizePathMode(pathMode) {
  const mode = pathMode == null || pathMode === '' ? 'normal' : String(pathMode);
  if (!VALID_PATH_MODES.includes(mode)) {
    throw new Error(
      `exploreUnknownWaypointDevStorage: pathMode must be "normal", "altMinus1", "altMinus2", "fork", "exitPreviewNormal", "exitPreviewAltMinus1", "exitPreviewAltMinus2", or "exitPreviewAlternative", got ${JSON.stringify(pathMode)}`
    );
  }
  return mode;
}

function getExpectedWaypointCount(surface) {
  const n = WAYPOINT_COUNT_BY_SURFACE[surface];
  if (typeof n !== 'number') {
    throw new Error(
      `exploreUnknownWaypointDevStorage: surface must be "arrival", "cave", "desert", "fair", "lavender", "treetop", "boat", "pixelpast", "kendrex", "autobahn", "productionhell", "awakening", or "core", got ${JSON.stringify(surface)}`
    );
  }
  return n;
}

function assertValidSurface(surface) {
  getExpectedWaypointCount(surface);
}

/**
 * @param {string} surface
 * @param {ExploreUnknownDevPathEditMode} [pathMode]
 * @returns {number}
 */
function getExpectedWaypointCountForPath(surface, pathMode = 'normal') {
  const mode = normalizePathMode(pathMode);
  const normal = getExpectedWaypointCount(surface);
  if (mode === 'normal') {
    return normal;
  }
  if (
    mode === 'fork' ||
    mode === 'exitPreviewNormal' ||
    mode === 'exitPreviewAltMinus1' ||
    mode === 'exitPreviewAltMinus2' ||
    mode === 'exitPreviewAlternative'
  ) {
    return 1;
  }
  if (mode === 'altMinus1') {
    return Math.max(2, normal - 1);
  }
  return Math.max(2, normal - 2);
}

/**
 * @param {string} surface
 * @param {ExploreUnknownDevPathEditMode} [pathMode]
 * @returns {string}
 */
function getDevWaypointStorageKey(surface, pathMode = 'normal') {
  assertValidSurface(surface);
  const mode = normalizePathMode(pathMode);
  if (mode === 'normal') {
    return `${STORAGE_KEY_PREFIX}${surface}`;
  }
  return `${STORAGE_KEY_PREFIX}${surface}_${mode}`;
}

function assertValidPoints(points, surface, pathMode = 'normal') {
  const expectedLen = getExpectedWaypointCountForPath(surface, pathMode);
  if (!Array.isArray(points) || points.length !== expectedLen) {
    throw new Error(
      `exploreUnknownWaypointDevStorage: points must be an array of length ${expectedLen}, got ${points?.length}`
    );
  }
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    if (!Array.isArray(p) || p.length !== 2) {
      throw new Error(`exploreUnknownWaypointDevStorage: point ${i} must be [x, y]`);
    }
    const [x, y] = p;
    if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) {
      throw new Error(`exploreUnknownWaypointDevStorage: point ${i} must be finite numbers`);
    }
  }
}

/**
 * @param {string} surface
 * @param {ExploreUnknownDevPathEditMode} [pathMode]
 * @returns {number[][] | null}
 */
function loadDevWaypoints(surface, pathMode = 'normal') {
  assertValidSurface(surface);
  const key = getDevWaypointStorageKey(surface, pathMode);
  let raw;
  try {
    raw = window.localStorage.getItem(key);
  } catch (err) {
    throw new Error(`exploreUnknownWaypointDevStorage: localStorage read failed (${err?.message ?? err})`);
  }
  if (raw == null || raw === '') {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(`exploreUnknownWaypointDevStorage: stored JSON is invalid (${err?.message ?? err})`);
  }
  assertValidPoints(parsed, surface, pathMode);
  return parsed;
}

/**
 * @param {string} surface
 * @param {number[][]} points
 * @param {ExploreUnknownDevPathEditMode} [pathMode]
 */
function saveDevWaypoints(surface, points, pathMode = 'normal') {
  assertValidSurface(surface);
  assertValidPoints(points, surface, pathMode);
  const key = getDevWaypointStorageKey(surface, pathMode);
  try {
    window.localStorage.setItem(key, JSON.stringify(points));
  } catch (err) {
    throw new Error(`exploreUnknownWaypointDevStorage: localStorage write failed (${err?.message ?? err})`);
  }
}

/**
 * @param {string} surface
 * @param {ExploreUnknownDevPathEditMode} [pathMode]
 */
function clearDevWaypoints(surface, pathMode = 'normal') {
  assertValidSurface(surface);
  const key = getDevWaypointStorageKey(surface, pathMode);
  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    throw new Error(`exploreUnknownWaypointDevStorage: localStorage remove failed (${err?.message ?? err})`);
  }
}

module.exports = {
  loadDevWaypoints,
  saveDevWaypoints,
  clearDevWaypoints,
  getExpectedWaypointCount,
  getExpectedWaypointCountForPath,
  getDevWaypointStorageKey,
  WAYPOINT_COUNT_BY_SURFACE,
};
